import { Document, Page, Text, View, StyleSheet, Image, pdf } from "@react-pdf/renderer";

const styles = StyleSheet.create({
  page: {
    fontFamily: "Helvetica",
    fontSize: 10,
    paddingTop: 48,
    paddingBottom: 48,
    paddingHorizontal: 56,
    color: "#1a1a1a",
  },

  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 32,
  },
  businessBlock: {
    maxWidth: "55%",
  },
  logoImage: {
    maxHeight: 48,
    maxWidth: 160,
    objectFit: "contain",
    marginBottom: 6,
  },
  businessName: {
    fontSize: 18,
    fontFamily: "Helvetica-Bold",
    color: "#2563eb",
    marginBottom: 4,
  },
  businessDetail: {
    fontSize: 9,
    color: "#555",
    lineHeight: 1.5,
  },
  estimateMeta: {
    alignItems: "flex-end",
  },
  estimateTitle: {
    fontSize: 26,
    fontFamily: "Helvetica-Bold",
    color: "#2563eb",
    marginBottom: 6,
  },
  metaRow: {
    flexDirection: "row",
    marginBottom: 2,
  },
  metaLabel: {
    fontSize: 9,
    color: "#888",
    width: 64,
    textAlign: "right",
    marginRight: 6,
  },
  metaValue: {
    fontSize: 9,
    fontFamily: "Helvetica-Bold",
    color: "#1a1a1a",
  },

  divider: {
    borderBottomWidth: 1,
    borderBottomColor: "#d1d5db",
    marginBottom: 20,
  },

  billSection: {
    marginBottom: 24,
  },
  sectionLabel: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: "#888",
    textTransform: "uppercase",
    letterSpacing: 1,
    marginBottom: 6,
  },
  clientName: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    marginBottom: 2,
  },
  clientDetail: {
    fontSize: 9,
    color: "#555",
    lineHeight: 1.5,
  },

  badgeAccepted: {
    backgroundColor: "#dbeafe",
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  badgeAcceptedText: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: "#1d4ed8",
  },
  badgeOpen: {
    backgroundColor: "#fef9c3",
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  badgeOpenText: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: "#a16207",
  },

  table: {
    marginBottom: 16,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f3f4f6",
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  tableRowAlt: {
    backgroundColor: "#f9fafb",
  },
  colName: { width: 100 },
  colDesc: { flex: 1 },
  colQty: { width: 48, textAlign: "right" },
  colPrice: { width: 72, textAlign: "right" },
  colTotal: { width: 72, textAlign: "right" },
  tableHeaderText: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: "#6b7280",
    textTransform: "uppercase",
  },
  tableBodyText: {
    fontSize: 9,
    color: "#1a1a1a",
  },

  totalsSection: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginBottom: 24,
  },
  totalsBox: {
    width: 200,
  },
  totalsRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 3,
  },
  totalsLabel: {
    fontSize: 9,
    color: "#555",
  },
  totalsValue: {
    fontSize: 9,
    color: "#1a1a1a",
  },
  totalsDivider: {
    borderBottomWidth: 1,
    borderBottomColor: "#d1d5db",
    marginVertical: 4,
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 4,
  },
  totalLabel: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
  },
  totalValue: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    color: "#2563eb",
  },

  notesSection: {
    marginBottom: 24,
  },
  notesText: {
    fontSize: 9,
    color: "#555",
    lineHeight: 1.6,
    borderLeftWidth: 2,
    borderLeftColor: "#d1d5db",
    paddingLeft: 8,
  },

  footer: {
    position: "absolute",
    bottom: 32,
    left: 56,
    right: 56,
    borderTopWidth: 1,
    borderTopColor: "#e5e7eb",
    paddingTop: 8,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  footerText: {
    fontSize: 8,
    color: "#9ca3af",
  },
});

const fmt = (n) => `$${(parseFloat(n) || 0).toFixed(2)}`;

const EstimatePDFDocument = ({ estimate }) => {
  const taxRate = parseFloat(estimate.tax_rate) || 0;

  return (
    <Document>
      <Page size="LETTER" style={styles.page}>

        {/* Header */}
        <View style={styles.header}>
          <View style={styles.businessBlock}>
            {estimate.business_logo_url ? <Image src={estimate.business_logo_url} style={styles.logoImage} /> : null}
            <Text style={styles.businessName}>{estimate.business_name || "Your Business"}</Text>
            {estimate.business_address ? <Text style={styles.businessDetail}>{estimate.business_address}</Text> : null}
            {(estimate.business_city || estimate.business_state) ? (
              <Text style={styles.businessDetail}>
                {estimate.business_city}{estimate.business_city && estimate.business_state ? ", " : ""}{estimate.business_state} {estimate.business_zip}
              </Text>
            ) : null}
            {estimate.business_phone ? <Text style={styles.businessDetail}>{estimate.business_phone}</Text> : null}
            {estimate.business_email ? <Text style={styles.businessDetail}>{estimate.business_email}</Text> : null}
          </View>

          <View style={styles.estimateMeta}>
            <Text style={styles.estimateTitle}>ESTIMATE</Text>
            <View style={styles.metaRow}>
              <Text style={styles.metaLabel}>Number</Text>
              <Text style={styles.metaValue}>#{estimate.estimate_number}</Text>
            </View>
            <View style={styles.metaRow}>
              <Text style={styles.metaLabel}>Date</Text>
              <Text style={styles.metaValue}>{estimate.date}</Text>
            </View>
            {estimate.due_date ? (
              <View style={styles.metaRow}>
                <Text style={styles.metaLabel}>Valid Until</Text>
                <Text style={styles.metaValue}>{estimate.due_date}</Text>
              </View>
            ) : null}
          </View>
        </View>

        <View style={styles.divider} />

        {/* Bill To + Status */}
        <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 24 }}>
          <View style={styles.billSection}>
            <Text style={styles.sectionLabel}>Prepared For</Text>
            <Text style={styles.clientName}>{estimate.client_name}</Text>
            {estimate.client_address ? <Text style={styles.clientDetail}>{estimate.client_address}</Text> : null}
            {(estimate.client_city || estimate.client_state || estimate.client_zip) ? (
              <Text style={styles.clientDetail}>
                {[estimate.client_city, estimate.client_state].filter(Boolean).join(", ")}{estimate.client_zip ? ` ${estimate.client_zip}` : ""}
              </Text>
            ) : null}
            {estimate.client_country ? <Text style={styles.clientDetail}>{estimate.client_country}</Text> : null}
            {estimate.client_email ? <Text style={styles.clientDetail}>{estimate.client_email}</Text> : null}
            {estimate.client_phone ? <Text style={styles.clientDetail}>{estimate.client_phone}</Text> : null}
          </View>

          <View>
            {estimate.accepted ? (
              <View style={styles.badgeAccepted}>
                <Text style={styles.badgeAcceptedText}>ACCEPTED{estimate.accepted_on ? ` · ${estimate.accepted_on}` : ""}</Text>
              </View>
            ) : (
              <View style={styles.badgeOpen}>
                <Text style={styles.badgeOpenText}>OPEN</Text>
              </View>
            )}
          </View>
        </View>

        {/* Line Items Table */}
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderText, styles.colName]}>Item</Text>
            <Text style={[styles.tableHeaderText, styles.colDesc]}>Description</Text>
            <Text style={[styles.tableHeaderText, styles.colQty]}>Qty</Text>
            <Text style={[styles.tableHeaderText, styles.colPrice]}>Unit Price</Text>
            <Text style={[styles.tableHeaderText, styles.colTotal]}>Total</Text>
          </View>
          {(estimate.line_items || []).map((item, i) => (
            <View key={i} style={[styles.tableRow, i % 2 === 1 ? styles.tableRowAlt : {}]}>
              <Text style={[styles.tableBodyText, styles.colName, { fontFamily: "Helvetica-Bold" }]}>{item.name || ""}</Text>
              <Text style={[styles.tableBodyText, styles.colDesc, { color: "#555" }]}>{item.description || ""}</Text>
              <Text style={[styles.tableBodyText, styles.colQty]}>{item.quantity}</Text>
              <Text style={[styles.tableBodyText, styles.colPrice]}>{fmt(item.unit_price)}</Text>
              <Text style={[styles.tableBodyText, styles.colTotal]}>{fmt(item.total)}</Text>
            </View>
          ))}
        </View>

        {/* Totals */}
        <View style={styles.totalsSection}>
          <View style={styles.totalsBox}>
            <View style={styles.totalsRow}>
              <Text style={styles.totalsLabel}>Subtotal</Text>
              <Text style={styles.totalsValue}>{fmt(estimate.subtotal)}</Text>
            </View>
            {taxRate > 0 && (
              <View style={styles.totalsRow}>
                <Text style={styles.totalsLabel}>Tax ({taxRate}%)</Text>
                <Text style={styles.totalsValue}>{fmt(estimate.tax_amount)}</Text>
              </View>
            )}
            <View style={styles.totalsDivider} />
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Estimate Total</Text>
              <Text style={styles.totalValue}>{fmt(estimate.total_amount)}</Text>
            </View>
          </View>
        </View>

        {/* Notes */}
        {estimate.notes ? (
          <View style={styles.notesSection}>
            <Text style={styles.sectionLabel}>Notes</Text>
            <Text style={styles.notesText}>{estimate.notes}</Text>
          </View>
        ) : null}

        {/* Footer */}
        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>Estimate #{estimate.estimate_number} · {estimate.business_name || ""}</Text>
          <Text style={styles.footerText}>Generated by PayClear</Text>
        </View>

      </Page>
    </Document>
  );
};

export const downloadEstimatePDF = async (estimate) => {
  const blob = await pdf(<EstimatePDFDocument estimate={estimate} />).toBlob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `Estimate-${estimate.estimate_number}-${estimate.client_name.replace(/\s+/g, "_")}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const getEstimatePDFBase64 = async (estimate) => {
  const blob = await pdf(<EstimatePDFDocument estimate={estimate} />).toBlob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};
